import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';

import { ControlledDatePicker } from '@/components/ControlledDatePicker';

interface EditDischargeDateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (date: Date) => void;
  isLoading: boolean;
  currentDate: string | null;
  patientName: string;
}

interface FormValues {
  discharge_date: Date | null;
}

function parseLocalDate(dateStr: string | null): Date | null {
  if (!dateStr) {
    return null;
  }
  // Append time to force local interpretation (YYYY-MM-DD alone is parsed as UTC)
  const parsed = dayjs(dateStr + 'T00:00:00');
  return parsed.isValid() ? parsed.toDate() : null;
}

export const EditDischargeDateModal: React.FC<EditDischargeDateModalProps> = ({
  open,
  onClose,
  onSave,
  isLoading,
  currentDate,
  patientName
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useForm<FormValues>({
    defaultValues: { discharge_date: parseLocalDate(currentDate) },
    mode: 'onChange'
  });

  useEffect(() => {
    if (open) {
      reset({ discharge_date: parseLocalDate(currentDate) });
    }
  }, [open, currentDate, reset]);

  const onSubmit = (data: FormValues) => {
    if (data.discharge_date) {
      onSave(data.discharge_date);
    }
  };

  const patientLabel = patientName ? ` for ${patientName}` : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Discharge Date{patientLabel}</DialogTitle>
      <DialogContent>
        <Stack pt={2} spacing={2}>
          <ControlledDatePicker
            control={control}
            name="discharge_date"
            label="Target Discharge Date"
            rules={{ required: 'Discharge date is required' }}
            disablePast
          />
          <Typography variant="body2" color="text.secondary">
            Recipients will see this as their target move-in date.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={!isValid}
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
